<template>
  <p class="mt-1 text-sm md:mt-2 md:text-base">
    <b>NUEVO:</b> Busca tus facturas o notas de crédito emitidas o ingresa el número manualmente.
    <a
      :href="`https://wa.me/${contactNumber}?text=¡Hola%20equipo%20Lokal!%20Necesito%20ayuda%20con%20la%20factura.`"
      class="hover:underline"
    >
      Cualquier duda contáctanos
      <span class="underline">aquí.</span>
    </a>
  </p>
  <div class="mt-2 flex items-center gap-2">
    <lokal-switch v-model="isAddingCreditNote" />
    <p class="text-sm font-light md:text-base">
      {{ 'Agregar Notas de Crédito' }}
    </p>
  </div>
  <div
    v-if="notConciliatedPurchaseInvoicesIsLoading"
    class="flex h-7 w-full rounded-md border border-gray-200 md:h-9"
  >
    <div
      class="size-full flex-1 animate-shimmer rounded-md bg-gradient-to-r from-gray-200 via-white to-gray-200 bg-[length:200%_100%]"
    ></div>
  </div>
  <template v-else-if="notConciliatedPurchaseInvoices?.length && notConciliatedPurchaseInvoices.length > 0">
    <div class="mt-2 flex w-full items-center gap-1 text-gray-700 md:mt-3">
      <p class="text-sm md:text-base">
        {{ isAddingCreditNote ? 'Notas de crédito detectadas:' : 'Facturas detectadas:' }}
      </p>
    </div>
    <lokal-generic-combobox
      v-model="selectedNotConciliatedPurchaseInvoices"
      :options="notConciliatedPurchaseInvoices"
      :get-option-key="(purchaseInvoice: PurchaseInvoice) => purchaseInvoice.id"
      :get-option-label="(purchaseInvoice: PurchaseInvoice) => notConciliatedPurchaseInvoiceLabel(purchaseInvoice)"
      compare-by="id"
      :options-label="`Selecciona una o más ${isAddingCreditNote ? 'notas de crédito' : 'facturas'}`"
      :multiple="true"
      class="font-light"
    />
  </template>
  <div
    v-if="notConciliatedPurchaseInvoices?.length && notConciliatedPurchaseInvoices.length > 0"
    class="mt-2 flex items-center gap-1"
  >
    <lokal-switch v-model="showPurchaseInvoiceNumberInput" />
    <p class="text-sm font-light md:text-base">
      {{ showPurchaseInvoiceNumberInput ? 'Ocultar' : 'Agregar' }} número de
      {{ isAddingCreditNote ? 'nota de crédito' : 'factura' }} manualmente
    </p>
  </div>
  <p
    v-else
    class="mt-2 text-sm font-light md:mt-3 md:text-base"
  >
    Ingresa un número de {{ isAddingCreditNote ? 'nota de crédito' : 'factura' }} manualmente
  </p>
  <div
    v-if="showPurchaseInvoiceNumberInput && !purchaseInvoiceConciliationsIsLoading"
    class="flex w-full flex-col"
  >
    <div
      class="mt-1 flex w-full items-center rounded-md border border-lk-light-gray text-sm focus-within:border-lk-green focus-within:outline-none md:text-base"
    >
      <Field
        v-model.number="purchaseInvoiceNumber"
        type="number"
        :placeholder="
          purchaseInvoiceConciliationsIsLoading
            ? 'Cargando...'
            : `Número de ${isAddingCreditNote ? 'nota de crédito' : 'factura'}`
        "
        name="purchaseInvoiceNumber"
        class="w-full flex-1 rounded-md p-1.5 font-light placeholder:text-sm focus:outline-none md:placeholder:text-base"
        :class="{
          'animate-shimmer bg-gradient-to-r from-gray-200 via-white to-gray-200': purchaseInvoiceConciliationsIsLoading,
          'h-full rounded-full bg-[length:200%_100%] focus:border-lk-light-gray': purchaseInvoiceConciliationsIsLoading,
        }"
        @keyup.enter.prevent="tryToAddAnotherPurchaseInvoice"
      />
      <button
        v-if="purchaseInvoiceNumber"
        class="mr-2 flex items-center gap-1 rounded-md border p-1 text-xs md:text-sm"
        type="button"
        @click="addAnotherPurchaseInvoice(purchaseInvoiceNumber)"
      >
        <PlusIcon class="size-2 fill-gray-700 md:size-3" />
        <span>Agregar</span>
      </button>
    </div>
    <ErrorMessage
      name="purchaseInvoiceNumbers"
      class="text-xs text-red-500 md:text-sm"
    />
  </div>
  <div
    v-if="!!purchaseInvoiceNumbers?.length"
    class="mt-2 flex flex-col text-sm md:mt-3 md:text-base"
  >
    <p>
      {{ isAddingCreditNote ? 'Número de notas de crédito seleccionadas:' : 'Número de facturas seleccionadas:' }}
    </p>
    <div class="flex flex-wrap gap-1 text-sm md:text-base">
      <button
        v-for="invoiceNumber in purchaseInvoiceNumbers"
        :key="`purchase-invoice-number-${invoiceNumber}`"
        class="flex items-center gap-1 rounded-full bg-lk-green px-2 py-0.5 text-xs text-white md:text-sm"
        type="button"
      >
        <span>{{ isAddingCreditNote ? 'NC' : 'F' }}#{{ invoiceNumber }}</span>
        <XMarkIcon
          class="size-2 fill-white md:size-3"
          @click="removeAnotherPurchaseInvoice(invoiceNumber)"
        />
      </button>
    </div>
  </div>
  <div
    v-if="!!conciliatedCreditNoteNumbers?.length"
    class="mt-2 flex flex-col text-sm md:mt-2 md:text-base"
  >
    <p>Notas de crédito asociadas:</p>
    <div class="flex flex-wrap gap-1 text-sm md:text-base">
      <button
        v-for="invoiceNumber in displayedCreditNotes"
        :key="`purchase-invoice-number-${invoiceNumber}`"
        class="flex items-center gap-1 rounded-full bg-lk-light-green px-2 py-0.5 text-xs text-white md:text-sm"
        type="button"
      >
        <span>NC#{{ invoiceNumber }}</span>
        <XMarkIcon
          class="size-2 fill-white md:size-3"
          @click="removeAnotherPurchaseInvoice(invoiceNumber)"
        />
      </button>
    </div>
  </div>
  <div class="mt-2 flex flex-col text-sm md:mt-2 md:text-base">
    <p
      v-for="invoiceNumber in purchaseInvoiceNumbersToDelete"
      :key="invoiceNumber"
      class="text-red-500"
    >
      <span>
        Estás eliminando {{ isInvoiceToDelete(invoiceNumber) ? 'la factura' : 'la nota de crédito' }} #{{
          invoiceNumber
        }}
      </span>
    </p>
  </div>
  <form
    v-if="purchaseInvoiceNumbersToDelete.length"
    class="flex w-full flex-col"
    :class="{ 'mt-3': showMakerInvoices, 'mt-auto': !showMakerInvoices }"
  >
    <Field
      v-model="purchaseInvoiceNumbersToDelete"
      name="purchaseInvoiceNumbersToDelete"
      label="Nuevos folios a eliminar"
      hidden
      :rules="{ required: true }"
    />
    <lokal-loader-button
      v-model="purchaseInvoiceConciliationsBulkDestroyIsLoading"
      :action-text="`Eliminar los folios: ${purchaseInvoiceNumbersToDelete.map((number) => `#${number}`).join(', ')}`"
      loading-text="Eliminando folios..."
      type="button"
      class="flex items-center justify-center rounded-md bg-lk-green p-1.5 text-sm text-white md:text-base"
      @click="purchaseInvoiceConciliationsBulkDestroyMutation.mutate()"
    />
  </form>
  <form
    v-else
    class="flex w-full flex-col"
    :class="{ 'mt-3': showMakerInvoices, 'mt-auto': !showMakerInvoices }"
    @submit="onSubmit"
  >
    <Field
      v-model="newPurchaseInvoiceNumbers"
      name="purchaseInvoiceNumbers"
      label="Nuevos folios"
      hidden
      :rules="{ required: true }"
    />
    <lokal-loader-button
      v-model="purchaseInvoiceConciliationsBulkCreateIsLoading"
      :action-text="actionText"
      :loading-text="loadingText"
      class="mt-auto flex items-center justify-center rounded-md bg-lk-green p-1.5 text-sm text-white md:text-base"
      :class="{
        'cursor-not-allowed opacity-80': Object.keys(errors).length > 0,
        'hover:bg-lk-green-deep': Object.keys(errors).length === 0,
      }"
      type="submit"
      :disabled="Object.keys(errors).length > 0"
    />
  </form>
</template>
<script setup lang="ts">
import { XMarkIcon, PlusIcon } from '@heroicons/vue/24/solid';
import { ref, computed, watch, onBeforeMount } from 'vue';
import { useQuery, useMutation } from 'vue-query';
import { useForm, Field, ErrorMessage } from 'vee-validate';
import type { AxiosError } from 'axios';
import purchaseInvoiceConciliationsApi from 'api/purchase-invoice-conciliations';
import purchaseInvoicesApi from 'api/purchase-invoices';
import useMessageStore from 'stores/message-store';
import lokalLoaderButton from './shared/lokal-loader-button.vue';
import lokalGenericCombobox from './shared/lokal-generic-combobox.vue';
import useFormatCurrency from './use/format-currency';
import lokalSwitch from './shared/lokal-switch.vue';

const ELECTRONIC_INVOICE_CODE_SII = ['30', '32', '33', '34'];
const DEFAULT_ELECTRONIC_INVOICE_CODE_SII = '33';
const CREDIT_NOTE_CODE_SII = '61';

interface PurchaseInvoicesConciliationFormProps {
  subOrder: SubOrder;
  maker: Maker;
}

const props = withDefaults(defineProps<PurchaseInvoicesConciliationFormProps>(), {});

// eslint-disable-next-line
const emit = defineEmits<{ (e: 'invoice-created'): void; (e: 'reload:subOrder'): void; (e: 'close-dialog'): void }>();

const messageStore = useMessageStore();
const contactNumber = computed(() => process.env.CONTACT_NUMBER);

const showMakerInvoices = ref(false);
const showPurchaseInvoiceNumberInput = ref(false);
const purchaseInvoiceNumber = ref<number | null>(null);
const purchaseInvoiceNumbers = ref<number[]>([]);
const purchaseInvoiceNumbersToDelete = ref<number[]>([]);
const isAddingCreditNote = ref(false);

const purchaseInvoiceConciliationQuery = useQuery(['purchase-invoice-conciliation', props.subOrder.id], () =>
  purchaseInvoiceConciliationsApi.index(props.maker.id, `q[sub_order_id_eq]=${props.subOrder.id}`),
);
const purchaseInvoiceConciliationsIsLoading = computed(() => purchaseInvoiceConciliationQuery.isLoading.value);
const purchaseInvoiceConciliations = computed(
  () =>
    purchaseInvoiceConciliationQuery.data.value?.data?.purchaseInvoiceConciliations as PurchaseInvoiceConciliation[],
);
const invoiceConciliatedNumbers = computed(() =>
  purchaseInvoiceConciliations.value
    ?.filter((invoice) => ELECTRONIC_INVOICE_CODE_SII.includes(invoice.codeSii))
    ?.map((invoice) => invoice.purchaseInvoiceNumber),
);
const conciliatedCreditNoteNumbers = computed(() =>
  purchaseInvoiceConciliations.value
    ?.filter((invoice) => invoice.codeSii === CREDIT_NOTE_CODE_SII)
    .map((invoice) => invoice.purchaseInvoiceNumber),
);

const notConciliatedPurchaseInvoicesQueryParams = computed(() => {
  const codeSiiParams = isAddingCreditNote.value
    ? 'q[code_sii_in][]=61'
    : 'q[code_sii_in][]=30&q[code_sii_in][]=32&q[code_sii_in][]=33&q[code_sii_in][]=34';

  let base = `${codeSiiParams}&`;
  const numbersToExclude = isAddingCreditNote.value
    ? conciliatedCreditNoteNumbers.value
    : invoiceConciliatedNumbers.value;

  numbersToExclude?.forEach((invoiceNumber) => {
    base += `q[number_not_in][]=${invoiceNumber}&`;
  });

  return base;
});
const notConciliatedPurchaseInvoicesQuery = useQuery(
  [
    'maker-not-conciliated-purchase-invoices',
    props.maker.id,
    notConciliatedPurchaseInvoicesQueryParams.value,
    isAddingCreditNote.value,
  ],
  () => purchaseInvoicesApi.notConciliated(props.maker.id, notConciliatedPurchaseInvoicesQueryParams.value),
  {
    onSuccess: (data) => {
      const purchaseInvoices = data?.data?.purchaseInvoices as PurchaseInvoice[];
      if (purchaseInvoices?.length === 0) {
        showPurchaseInvoiceNumberInput.value = true;
      }
    },
  },
);
const notConciliatedPurchaseInvoicesIsLoading = computed(() => notConciliatedPurchaseInvoicesQuery.isLoading.value);
const notConciliatedPurchaseInvoices = computed(
  () => notConciliatedPurchaseInvoicesQuery.data.value?.data?.purchaseInvoices as PurchaseInvoice[],
);
const selectedNotConciliatedPurchaseInvoices = ref<PurchaseInvoice[]>([]);

watch(selectedNotConciliatedPurchaseInvoices, (purchaseInvoices, oldPurchaseInvoices) => {
  const addedPurchaseInvoices = purchaseInvoices.filter(
    (purchaseInvoice) => !oldPurchaseInvoices.includes(purchaseInvoice),
  );
  addedPurchaseInvoices.forEach((purchaseInvoice) => {
    if (!purchaseInvoiceNumbers.value.includes(purchaseInvoice.number)) {
      purchaseInvoiceNumbers.value.push(purchaseInvoice.number);
    }
  });
  const removedPurchaseInvoices = oldPurchaseInvoices.filter(
    (purchaseInvoice) => !purchaseInvoices.includes(purchaseInvoice),
  );
  removedPurchaseInvoices.forEach((purchaseInvoice) => {
    if (purchaseInvoiceNumbers.value.includes(purchaseInvoice.number)) {
      purchaseInvoiceNumbers.value = purchaseInvoiceNumbers.value.filter((number) => number !== purchaseInvoice.number);
    }
  });
});

const newPurchaseInvoiceNumbers = computed(
  () => purchaseInvoiceNumbers.value?.filter((number) => !invoiceConciliatedNumbers.value?.includes(number)) || [],
);

const bulkCreateParams = computed(() => {
  const filteredPurchaseInvoiceNumbers = purchaseInvoiceNumbers.value?.filter((invoiceNumber) => {
    const existingNumbers = isAddingCreditNote.value
      ? conciliatedCreditNoteNumbers.value
      : invoiceConciliatedNumbers.value;
    return !existingNumbers?.includes(invoiceNumber);
  });

  return filteredPurchaseInvoiceNumbers?.map((invoiceNumber) => ({
    makerId: props.maker.id,
    subOrderId: props.subOrder.id,
    purchaseInvoiceNumber: invoiceNumber,
    codeSii: isAddingCreditNote.value ? CREDIT_NOTE_CODE_SII : DEFAULT_ELECTRONIC_INVOICE_CODE_SII,
  }));
});
const purchaseInvoiceConciliationsBulkCreateMutation = useMutation(
  (params: Partial<PurchaseInvoiceConciliation>[]) => purchaseInvoiceConciliationsApi.bulkCreate(params),
  {
    onSuccess: () => {
      const message = `Folios confirmados correctamente, tus facturas se están validando
        ${purchaseInvoiceNumbers.value.map((number) => `#${number}`).join(', ')}`;
      messageStore.showMessage(message, 'success');
      emit('reload:subOrder');
      emit('close-dialog');
    },
    onError: (error: AxiosError, variables: Partial<PurchaseInvoiceConciliation>[]) => {
      let baseMessage = '';
      const axiosError = error as AxiosError<{ errors: { purchase_invoice_number: string[] } }>;
      axiosError?.response?.data?.errors?.purchase_invoice_number?.forEach((currentError, index) => {
        baseMessage += `Número de factura: ${variables[index]?.purchaseInvoiceNumber} | error: ${currentError}\n`;
      });
      messageStore.showMessage(`Error al crear las conciliaciones de facturas: ${baseMessage}`, 'error');
    },
  },
);
const purchaseInvoiceConciliationsBulkCreateIsLoading = computed(
  () => purchaseInvoiceConciliationsBulkCreateMutation.isLoading.value,
);
const { handleSubmit, validate, errors } = useForm();

const purchaseInvoiceConciliationsBulkDestroyParams = computed(() => ({
  makerId: props.maker.id,
  purchaseInvoiceNumbers: purchaseInvoiceNumbersToDelete.value,
}));
const purchaseInvoiceConciliationsBulkDestroyMutation = useMutation(
  () => purchaseInvoiceConciliationsApi.bulkDestroy(purchaseInvoiceConciliationsBulkDestroyParams.value),
  {
    onSuccess: async () => {
      const message = `Folios eliminados correctamente, tus facturas se están validando
        ${purchaseInvoiceNumbersToDelete.value.map((number) => `#${number}`).join(', ')}`;
      messageStore.showMessage(message, 'success');
      await purchaseInvoiceConciliationQuery.refetch.value();
      purchaseInvoiceNumbersToDelete.value = [];
    },
  },
);
const purchaseInvoiceConciliationsBulkDestroyIsLoading = computed(
  () => purchaseInvoiceConciliationsBulkDestroyMutation.isLoading.value,
);

const onSubmit = handleSubmit(() => {
  purchaseInvoiceConciliationsBulkCreateMutation.mutate(bulkCreateParams.value);
});

async function addAnotherPurchaseInvoice(invoiceNumber: number) {
  if (invoiceNumber === props.subOrder.id) {
    messageStore.showMessage('No se puede agregar la suborden como factura', 'error');

    return;
  }
  if (purchaseInvoiceNumbers.value.includes(invoiceNumber)) return;

  purchaseInvoiceNumbers.value.push(invoiceNumber);
  purchaseInvoiceNumber.value = null;
  await validate();
}

function tryToAddAnotherPurchaseInvoice() {
  if (purchaseInvoiceNumber.value) addAnotherPurchaseInvoice(purchaseInvoiceNumber.value);
}

function isInvoiceToDelete(invoiceNumber: number) {
  return invoiceConciliatedNumbers.value?.includes(invoiceNumber) || false;
}

function removeAnotherPurchaseInvoice(invoiceNumber: number) {
  purchaseInvoiceNumbers.value = purchaseInvoiceNumbers.value.filter((number) => number !== invoiceNumber);
  if (
    invoiceConciliatedNumbers.value?.includes(invoiceNumber) ||
    conciliatedCreditNoteNumbers.value?.includes(invoiceNumber)
  ) {
    purchaseInvoiceNumbersToDelete.value.push(invoiceNumber);
  }
  selectedNotConciliatedPurchaseInvoices.value = selectedNotConciliatedPurchaseInvoices.value.filter(
    (invoice) => invoice.number !== invoiceNumber,
  );
}

function addPurchaseInvoiceConciliatedNumbers() {
  if (purchaseInvoiceConciliations.value?.length) {
    invoiceConciliatedNumbers.value?.forEach((invoiceNumber) => {
      if (purchaseInvoiceNumbers.value.includes(invoiceNumber)) return;

      purchaseInvoiceNumbers.value.push(invoiceNumber);
    });
  }
}
watch(purchaseInvoiceConciliations, addPurchaseInvoiceConciliatedNumbers);
onBeforeMount(() => {
  addPurchaseInvoiceConciliatedNumbers();
});

const actionText = computed(() => {
  const base = newPurchaseInvoiceNumbers.value?.length ? 'Actualizar' : 'Confirmar';

  return `${base} los folios: ${newPurchaseInvoiceNumbers.value.map((number) => `#${number}`).join(', ')}`;
});
const loadingText = computed(() => {
  const base = newPurchaseInvoiceNumbers.value?.length ? 'Actualizando' : 'Confirmando';

  return `${base} los folios...`;
});

function notConciliatedPurchaseInvoiceLabel(purchaseInvoice?: PurchaseInvoice) {
  if (purchaseInvoice?.id) {
    const docType = isAddingCreditNote.value ? 'Nota de Crédito' : 'Factura';
    const withoutShipping = purchaseInvoice.netAmount - purchaseInvoice.shippingCost;
    const label = `${docType} #${purchaseInvoice.number} | Neto: ${useFormatCurrency(purchaseInvoice.netAmount)}`;
    return `${label} | Neto SIN despacho: ${useFormatCurrency(withoutShipping)}`;
  }

  return '';
}
watch(isAddingCreditNote, async () => {
  purchaseInvoiceNumbers.value = [];
  selectedNotConciliatedPurchaseInvoices.value = [];
  purchaseInvoiceNumber.value = null;
  showPurchaseInvoiceNumberInput.value = false;
  await notConciliatedPurchaseInvoicesQuery.refetch.value();
});

const displayedCreditNotes = computed(
  () =>
    conciliatedCreditNoteNumbers.value?.filter((number) => !purchaseInvoiceNumbersToDelete.value.includes(number)) ||
    [],
);
</script>
